import { motion } from "framer-motion";
import { Users, Radar, ShieldCheck, Building2, Ambulance, Bus, TrainFront, School, HeartPulse } from "lucide-react";

const NODES = [
  { id: "person", label: "Person", icon: Users, level: 0 },
  { id: "grid", label: "LIFEGRID", icon: Radar, level: 1 },
  { id: "trust", label: "Trust Circle", icon: ShieldCheck, level: 2 },
  { id: "community", label: "Community", icon: Building2, level: 3 },
];

const FUTURE = [
  { icon: Ambulance, label: "Emergency Services" },
  { icon: HeartPulse, label: "Hospitals" },
  { icon: School, label: "Schools" },
  { icon: ShieldCheck, label: "Public Safety" },
  { icon: Users, label: "Accessibility Services" },
  { icon: Bus, label: "Transport" },
  { icon: TrainFront, label: "Disaster Response" },
];

export function Vision() {
  return (
    <section id="vision" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="absolute inset-0 tac-grid pointer-events-none opacity-30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 11 // NETWORK</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-4xl">
          Life should never come with a <span className="text-lime-accent">&quot;what now?&quot;</span>
        </h2>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            { t: "Understand", d: "Turn confusing situations into understandable steps." },
            { t: "Act", d: "Show what to do right now." },
            { t: "Connect", d: "Help people communicate their situation." },
          ].map((p) => (
            <div key={p.t} className="glass rounded-md p-6">
              <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">PRINCIPLE</div>
              <div className="mt-2 font-display text-3xl font-extrabold uppercase">{p.t}</div>
              <p className="mt-2 text-[color:var(--lg-text-2)]">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2 space-y-6">
            {NODES.map((n, i) => {
              const Icon = n.icon;
              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4"
                >
                  <div
                    className="h-14 w-14 rounded-md border border-white/15 grid place-items-center"
                    style={{ boxShadow: "0 0 24px rgba(204,255,0,0.12)" }}
                  >
                    <Icon size={22} className="text-[color:var(--lg-lime)]" />
                  </div>
                  <div>
                    <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
                      LEVEL {String(n.level).padStart(2, "0")}
                    </div>
                    <div className="font-display text-xl font-extrabold uppercase">{n.label}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            <div className="glass rounded-md p-6">
              <div className="flex items-center justify-between">
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">THE LIFEGRID NETWORK</div>
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">FUTURE</div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {FUTURE.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.label}
                      className="rounded-sm border border-white/10 p-3 flex items-center gap-3 hover:border-[color:var(--lg-lime)]/40"
                      data-testid={`future-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon size={18} className="text-[color:var(--lg-cyan)]" />
                      <span className="text-sm">{f.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-amber)]">
                <span className="h-2 w-2 rounded-full bg-[color:var(--lg-amber)] animate-pulse" />
                FUTURE INTEGRATIONS — NOT CURRENTLY CONNECTED
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
